/**
 * Product Tab Reviews Custom Element
 * A web component for product review tab interface
 */
class ProductTabReviewsElement extends HTMLElement {
  constructor() {
    super();

    // Bind methods to maintain proper context
    this.switchTab = this.switchTab.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  static get observedAttributes() {
    return ['active-tab', 'setup-delay'];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'active-tab' && this.isConnected) {
      this.switchToTab(newValue);
    }
  }

  init() {
    // Cache DOM elements
    this.cacheElements();

    this.bindTabSwitching();
    this.setupInitialState();
  }

  cacheElements() {
    this.tabs = this.querySelectorAll('.tab-button');
    this.contents = this.querySelectorAll('.tab-content');
  }

  bindTabSwitching() {
    this.tabs.forEach(tab => {
      // Remove existing listeners to prevent duplicates
      tab.removeEventListener('click', this.switchTab);
      tab.removeEventListener('keydown', this.handleKeydown);

      // Add event listeners
      tab.addEventListener('click', this.switchTab);
      tab.addEventListener('keydown', this.handleKeydown);
    });
  }

  handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.switchTab(e);
    }
  }

  setupInitialState() {
    // Get setup delay from attribute or default
    const setupDelay = parseInt(this.getAttribute('setup-delay')) || 1200;

    // Set up initial visibility with a delay to ensure proper rendering
    this.contents.forEach((content, index) => {
      setTimeout(() => {
        // Add hidden class to all but first item
        if (index > 0) {
          content.classList.add('vw-hidden');
        }
      }, setupDelay);
    });
  }

  switchTab(event) {
    try {
      const selectedTab = event.currentTarget;
      const tabId = selectedTab.dataset.tabButton;

      // Update tab button states
      this.tabs.forEach(tab => {
        tab.classList.toggle('active', tab === selectedTab);
      });

      // Update content visibility
      this.contents.forEach(content => {
        const isTargetContent = content.dataset.tabContent === tabId;

        content.classList.toggle('vw-hidden', !isTargetContent);
        content.classList.toggle('active', isTargetContent);
      });

      // Update attribute to reflect active tab
      this.setAttribute('active-tab', tabId);

      // Dispatch custom event for external listeners
      this.dispatchEvent(new CustomEvent('product-reviews:tab-switch', {
        detail: { activeTab: tabId },
        bubbles: true
      }));
    } catch (error) {
      console.error('Error switching product review tabs:', error);
    }
  }

  // Public API methods
  /**
   * Programmatically switch to a specific tab
   */
  switchToTab(tabId) {
    const targetTab = this.querySelector(`.tab-button[data-tab-button="${tabId}"]`);
    if (targetTab) {
      targetTab.click();
    }
  }

  /**
   * Get currently active tab
   */
  get activeTab() {
    return this.getAttribute('active-tab');
  }

  /**
   * Get currently active tab element
   */
  getActiveTabElement() {
    return this.querySelector('.tab-button.active');
  }

  /**
   * Get all available tabs
   */
  getAllTabs() {
    return Array.from(this.tabs).map(tab => ({
      id: tab.dataset.tabButton,
      label: tab.textContent.trim(),
      element: tab
    }));
  }

  /**
   * Set setup delay for initial state
   */
  set setupDelay(value) {
    this.setAttribute('setup-delay', value.toString());
  }

  /**
   * Get setup delay
   */
  get setupDelay() {
    return parseInt(this.getAttribute('setup-delay')) || 1200;
  }

  /**
   * Cleanup method
   */
  cleanup() {
    if (this.tabs) {
      this.tabs.forEach(tab => {
        tab.removeEventListener('click', this.switchTab);
        tab.removeEventListener('keydown', this.handleKeydown);
      });
    }
  }
}

// Register the custom element
if (!customElements.get('product-tab-reviews')) {
  customElements.define('product-tab-reviews', ProductTabReviewsElement);
}

// Legacy support - convert existing elements to custom elements
function upgradeExistingProductTabReviewsElements() {
  const legacyElements = document.querySelectorAll('.product-tab-reviews:not(product-tab-reviews)');
  legacyElements.forEach(element => {
    // Create new custom element
    const customElement = document.createElement('product-tab-reviews');

    // Copy attributes
    Array.from(element.attributes).forEach(attr => {
      customElement.setAttribute(attr.name, attr.value);
    });

    // Copy classes and maintain them
    customElement.className = element.className;

    // Move content
    customElement.innerHTML = element.innerHTML;

    // Replace in DOM
    element.parentNode.replaceChild(customElement, element);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', upgradeExistingProductTabReviewsElements);
} else {
  upgradeExistingProductTabReviewsElements();
}

// Handle Shopify section events for theme editor
document.addEventListener('shopify:section:load', function(event) {
  setTimeout(() => {
    const legacyElements = event.target.querySelectorAll('.product-tab-reviews:not(product-tab-reviews)');
    legacyElements.forEach(element => {
      const customElement = document.createElement('product-tab-reviews');
      Array.from(element.attributes).forEach(attr => {
        customElement.setAttribute(attr.name, attr.value);
      });
      customElement.className = element.className;
      customElement.innerHTML = element.innerHTML;
      element.parentNode.replaceChild(customElement, element);
    });
  }, 0);
});

document.addEventListener('shopify:block:select', function(event) {
  const blockElement = event.target;
  const productTabReviewsElement = blockElement.closest('product-tab-reviews');

  if (productTabReviewsElement) {
    // Find which tab this block belongs to and switch to it
    const tabContent = blockElement.closest('.tab-content');
    if (tabContent) {
      const tabId = tabContent.dataset.tabContent;
      productTabReviewsElement.switchToTab(tabId);
    }
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductTabReviewsElement;
}